import { Box, LinearProgress, Popover, Typography } from '@mui/material';
import {
  DataGridPro,
  GridCellParams,
  GridRowClassNameParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from '@mui/x-data-grid-pro';
import { useI2pDataGridEdit, useI2pSimpleDataGrid } from '@price-for-profit/data-grid';
import { useEffect, useMemo, useState } from 'react';
import { QUERY_KEYS, RESPONSE_YES } from 'shared/constants';
import { useUpdateProduct, useUserActivityEvent } from 'shared/mutations';
import { useService } from 'shared/providers';
import { CommoditySqlView, UserInfo } from 'shared/types';
import { determineIsAdmin } from 'shared/utility';
import { columns } from './columns';
import { NoRowsOverlay } from './no-rows-overlay';
import { ProductDetailDrawer } from './product-detail-drawer';
import { ProductsTableToolbar } from './products-table-toolbar';
import { useProductState } from './state';

interface ProductsTableProps {
  user: UserInfo;
  isProductSummaryCollapsed: boolean;
}
export function ProductsTable({ user, isProductSummaryCollapsed }: ProductsTableProps) {
  const userTracking = useUserActivityEvent();
  const { productService } = useService();
  const isAdmin = determineIsAdmin(user);
  const state = useProductState();
  const [detailRow, setDetailRow] = useState<CommoditySqlView>({} as CommoditySqlView);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const updateMutation = useUpdateProduct();

  const productTableHeight = useMemo<string>(
    () => `calc(100vh - ${isProductSummaryCollapsed ? 215 : 145}px)`,
    [isProductSummaryCollapsed]
  );

  const { getDataGridProps } = useI2pSimpleDataGrid<CommoditySqlView>({
    columns,
    name: QUERY_KEYS.commodities,
    dataGridInitialState: {
      sorting: {
        sortModel: [{ field: 'lastWeekCasesSold', sort: 'desc' }],
      },
      columns: {
        columnVisibilityModel: {
          [GRID_CHECKBOX_SELECTION_COL_DEF.field]: true,
          // The following line is a hack to force datagridpro to store the initial columnVisibilityModel as
          // part of the apiRef.current.state so it can be reverted to in the default view for the toolbar.
          xxx: false,
        },
      },
      pinnedColumns: {
        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'commodity', 'description', 'productNumber'],
      },
    },
    getData: async () => {
      return productService.getGridData(isAdmin ? undefined : user.id);
    },
  });

  const dataGridProps = getDataGridProps();
  const { apiRef } = dataGridProps;

  useEffect(() => {
    // Subscribe to events
    return apiRef?.current.subscribeEvent('cellClick', (params: GridCellParams<CommoditySqlView>) => {
      if (params.field === 'productNumber') {
        setDetailRow(params.row);
        userTracking.mutateAsync({
          action: 'DecisionSupport',
          eventType: 'ButtonClick',
          data: { itemID: params.row.productNumber },
        });
      }
    });
  }, [apiRef, userTracking]);

  useEffect(() => {
    // Subscribe to events
    return apiRef?.current.subscribeEvent('sortModelChange', params => {
      userTracking.mutateAsync({
        action: 'Sort',
        eventType: 'ButtonClick',
        data: { columns: params },
      });
    });
  }, [apiRef, userTracking]);

  useEffect(() => {
    return apiRef?.current.subscribeEvent('selectionChange', params => {
      state.selectedIds.set(params as string[]);
    });
  }, [apiRef, state.selectedIds]);

  const { getDataGridEditProps } = useI2pDataGridEdit<CommoditySqlView>({
    onEdit: async (newRow, oldRow) => {
      if (newRow.revisedCost !== oldRow.revisedCost) {
        userTracking.mutateAsync({
          action: 'EditValueCost',
          eventType: 'ButtonClick',
          data: {
            itemID: newRow.id,
            oldValue: oldRow.revisedCost,
            newValue: newRow.revisedCost,
          },
        });
      }

      if (oldRow.revisedBestPrice !== newRow.revisedBestPrice) {
        userTracking.mutateAsync({
          action: 'EditValuePrice',
          eventType: 'ButtonClick',
          data: {
            itemID: newRow.id,
            oldValue: oldRow.revisedBestPrice,
            newValue: newRow.revisedBestPrice,
          },
        });
      }

      if (oldRow.revisedSpotBestPrice !== newRow.revisedSpotBestPrice) {
        userTracking.mutateAsync({
          action: 'EditValueSpotPrice',
          eventType: 'ButtonClick',
          data: {
            itemID: newRow.id,
            oldValue: oldRow.revisedSpotBestPrice,
            newValue: newRow.revisedSpotBestPrice,
          },
        });
      }

      if (newRow.revisedCost !== oldRow.revisedCost) {
        if (oldRow.recommendedBestPrice === oldRow.revisedBestPrice) {
          newRow.revisedBestPrice = newRow.revisedCost + newRow.recommendedBestPriceMarginPerCase;
        }
      }

      const updatedRow = await updateMutation.mutateAsync({ newRow, oldRow, user });
      return updatedRow;
    },
    onError: (error: Error) => {
      console.log({ children: error.message, severity: 'error' });
    },
  });

  const closeDrawer = () => {
    setDetailRow({} as CommoditySqlView);
  };

  const handlePopoverOpen = (event: any) => {
    if (event.currentTarget.dataset.field === 'belowMarginFloor' && event.currentTarget.innerText === RESPONSE_YES) {
      setPopoverAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
  };

  return (
    <Box
      height={productTableHeight}
      sx={{
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
        },
        '& .drive-app-wps--cell--editable': {
          bgcolor: '#dbe7f2',
        },
      }}
    >
      <DataGridPro
        {...dataGridProps}
        {...getDataGridEditProps()}
        // custom density toolbar
        density='compact'
        localeText={{
          toolbarDensity: 'Row Spacing',
        }}
        checkboxSelection
        disableSelectionOnClick
        components={{
          Toolbar: ProductsTableToolbar,
          NoRowsOverlay: NoRowsOverlay,
          LoadingOverlay: LinearProgress,
        }}
        componentsProps={{
          toolbar: { user },
          noRowsOverlay: { isAdmin },
          cell: {
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
          },
        }}
        disableVirtualization={false}
        getRowClassName={(params: GridRowClassNameParams<CommoditySqlView>) =>
          params.row.productNumber === detailRow.productNumber ? 'detail-row' : ''
        }
        headerHeight={80}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            textOverflow: 'clip',
            whiteSpace: 'break-spaces',
            lineHeight: 1,
          },
        }}
      />
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Typography
          sx={{ p: 1 }}
        >{`The margin per case at this price is below the recommended margin floor`}</Typography>
      </Popover>
      {detailRow && <ProductDetailDrawer productDetail={detailRow} closeDrawer={closeDrawer} />}
    </Box>
  );
}
